import { PROJCOMP, CCO } from "../_constants/actionConstants";
import axios from "axios";
import { BASE_URL } from "../_constants/urlConstants";
import { getUUID } from "../_utils/generalUtils";
import { actionScripts } from "../_helpers/actionScripts";
import raiseError from "./raiseError";
import raiseWarning from "./raiseWarning";
import bidItemActions from "./bidItemActions";
import qSheetActions from "./qSheetActions";
import payEstimateActions from "./payEstimateActions";

// Re-fetches all open QSheet tabs and pay estimate detail tabs so that
// CCO Qty / Total Authorized Qty / Auth. Qty update immediately after any
// CCO mutation (add, update, delete) without requiring a manual refresh.
const refreshOpenPayEstimateData = () => (dispatch, getState) => {
  const { openQSheets, openEstimateDetails } = getState().payEstimate;

  Object.entries(openQSheets).forEach(([tabId, qs]) => {
    if (qs?.qSheetId) dispatch(qSheetActions.fetchQSheet(qs.qSheetId, tabId));
  });

  Object.entries(openEstimateDetails).forEach(([tabId, detail]) => {
    if (detail?.payEstimateId) dispatch(payEstimateActions.fetchDetail(detail.payEstimateId, tabId));
  });
};

const add = (newCcoData) => async (dispatch) => {
  dispatch({ type: CCO.ADD });
  const apiEndpoint = `${BASE_URL}/api/Ccos`;
  try {
    const res = await axios.post(apiEndpoint, newCcoData);
    if (res.data.message) {
      dispatch({ type: CCO.ADD_REJECTED });
      raiseWarning(CCO.ADD_REJECTED, res.data.message);
      return null;
    }
    dispatch({ type: CCO.ADD_FULFILLED });
    dispatch(bidItemActions.getAll(newCcoData.projectId));
    dispatch(refreshOpenPayEstimateData());
    return res.data;
  } catch (err) {
    dispatch({ type: CCO.ADD_REJECTED });
    raiseError(CCO.ADD_REJECTED, err.response.data);
  }
};

const update = (ccoData) => async (dispatch) => {
  dispatch({ type: CCO.EDIT });
  const apiEndpoint = `${BASE_URL}/api/Ccos`;
  try {
    const res = await axios.put(apiEndpoint, ccoData);
    if (res.data.message) {
      dispatch({ type: CCO.EDIT_REJECTED });
      raiseWarning(CCO.EDIT_REJECTED, res.data.message);
      return null;
    }
    dispatch({
      type: CCO.EDIT_FULFILLED,
      ccoDetails: res.data,
      projectId: ccoData.projectId,
      ccoId: ccoData.ccoId,
    });
    dispatch(getAll(ccoData.projectId));
    dispatch(bidItemActions.getAll(ccoData.projectId));
    dispatch(refreshOpenPayEstimateData());
    return true;
  } catch (err) {
    dispatch({ type: CCO.EDIT_REJECTED });
    raiseError(CCO.EDIT_REJECTED, err.response?.data);
  }
};

const deleteCco = (ccoId, projectId) => async (dispatch) => {
  dispatch({ type: CCO.DELETE });
  const apiEndpoint = `${BASE_URL}/api/Ccos/${ccoId}/${projectId}`;
  try {
    await axios.delete(apiEndpoint);
    dispatch({
      type: CCO.DELETE_FULFILLED,
      projectId,
    });
    dispatch({ type: CCO.CLEAR_DETAILS, projectId, ccoId });
    dispatch(bidItemActions.getAll(projectId));
    dispatch(refreshOpenPayEstimateData());
    return;
  } catch (err) {
    dispatch({ type: CCO.DELETE_REJECTED });
    raiseError(CCO.DELETE_REJECTED, err.response.data);
  }
};

const hasLinks = (ccoId) => async (dispatch) => {
  dispatch({ type: CCO.GENERIC_REQUEST });
  const apiEndpoint = BASE_URL + "/api/Ccos/HasLinks/?ccoId=" + ccoId;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: CCO.GENERIC_REQUEST_FULFILLED });
    return res.data;
  } catch (err) {
    const errorId = getUUID();
    const type = PROJCOMP.CCO_HAS_LINKS_REJECTED;
    const newError = { errorId, type, error: err.response.data };
    actionScripts.ADD_ERROR.run(newError);
    console.log(PROJCOMP.CCO_HAS_LINKS_REJECTED, err.response.data);
  }
};

const getDetails = (ccoId, projectId) => async (dispatch) => {
  dispatch({ type: CCO.FETCH_DETAILS });
  const apiEndpoint = `${BASE_URL}/api/Ccos/${ccoId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({
      type: CCO.FETCH_DETAILS_FULFILLED,
      projectId,
      ccoId,
      ccoDetails: res.data,
    });
    return;
  } catch (err) {
    dispatch({ type: CCO.FETCH_DETAILS_REJECTED });
    raiseError(CCO.FETCH_DETAILS_REJECTED, err.response.data);
  }
};

const getAll = (projectId) => async (dispatch) => {
  dispatch({ type: CCO.FETCH_ALL });
  const apiEndpoint = `${BASE_URL}/api/Ccos/List/${projectId}`;
  try {
    const res = await axios.get(apiEndpoint);
    dispatch({ type: CCO.FETCH_ALL_FULFILLED, projectId, projCcoList: res.data });
    return;
  } catch (err) {
    dispatch({ type: CCO.FETCH_ALL_REJECTED });
    raiseError(CCO.FETCH_ALL_REJECTED, err.response.data);
  }
};

const ccoActions = {
  add,
  getAll,
  getDetails,
  hasLinks,
  deleteCco,
  update,
};

export default ccoActions;
